import React from 'react';
import { Interp, css, toList } from '../../lib/render.jsx';
import { Herstel, Kader, Potlood } from '../Iconen.jsx';
import Venster from '../Venster.jsx';
import { iconStroke } from '../../lib/icons.js';

/**
 * Krabbellijn.
 *
 * Geen dialoogvenster maar een programmaatje: één kader met een eigen titelbalk,
 * dat je aan die balk oppakt en neerzet waar je wil. Er zit geen donkere laag
 * omheen, want zolang je een tekenbeurt hebt moet je het doek eronder kunnen zien.
 *
 * Hij staat standaard onderaan in het midden, op de plek waar in het tekenscherm
 * het thema van vandaag hangt. Daar kijk je toch al.
 *
 * Het kruisje breekt je beurt af: wat je had is dan weg, en de beurt blijft bij
 * jou liggen tot de klok afloopt. Zie closeKetting in logic/ketting.js.
 */
export default function Ketting({ v }) {
  if (!v.showKet) return null;
  return (
    <Venster
      v={v} venster={v.ketVenster} titel={v.ketTitel} onSluit={v.closeKetting}
      naast={v.showKetKlok ? <span style={css(v.ketKlokStyle)}><Interp v={v.ketKlokLabel} /></span> : null}
      extra={(
        /* De geluidsknop hoort bij dit spel: er loopt een muziekje terwijl je tekent.
           Hij zet `spelStil` om, dezelfde vlag als in Krabbelry en Krabbelworm. */
        <button onClick={v.toggleSpelStil} title={v.spelStilTitel} aria-label={v.spelStilTitel} aria-pressed={v.spelStil ? 'true' : 'false'} style={css(v.ketVenster?.knopStyle)}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={iconStroke(20)} strokeLinecap="round" strokeLinejoin="round">
            <path d="M11 5 6 9H2v6h4l5 4V5z" />
            {v.spelStil ? <path d="m23 9-6 6M17 9l6 6" /> : <path d="M19.1 4.9a10 10 0 0 1 0 14.2M15.5 8.5a5 5 0 0 1 0 7" />}
          </svg>
        </button>
      )}
    >
        <div style={css(v.ketInhoudStyle)}>
          {v.showKetLeeg ? (
            <div style={css(v.ketLeegStyle)}><Interp v={v.ketLeeg} /></div>
          ) : null}

          {v.showKetLaden ? (
            <div style={css(v.ketLadenStyle)}><Interp v={v.ketLaden} /></div>
          ) : null}

          {v.showKetDoe ? (
            <React.Fragment>
              <div style={css(v.ketStipRijStyle)}>
                {toList(v.ketStippen).map((s, $index) => (
                  <React.Fragment key={$index}>
                    <span style={css(s?.style)} />
                  </React.Fragment>
                ))}
              </div>

              {v.showKetVorigeZin ? (
                <div style={css(v.ketVorigeZinStyle)}><Interp v={v.ketVorigeZin} /></div>
              ) : null}
              {v.showKetVorigeKrabbel ? (
                <img alt="De tekening van de vorige" src={v.ketVorigeSrc} style={css(v.ketVorigeKrabbelStyle)} />
              ) : null}

              {v.showKetTypen ? (
                <React.Fragment>
                  <input value={v.ketZin} onChange={v.ketTyp} onKeyDown={v.ketZinToets} maxLength={v.ketZinMax} placeholder={v.ketZinPlaats} style={css(v.ketZinStyle)} />
                  {v.showKetZinTel ? (
                    <div style={css(v.ketZinTelStyle)}><Interp v={v.ketZinTel} /></div>
                  ) : null}
                </React.Fragment>
              ) : null}

              {v.showKetDoek ? (
                <React.Fragment>
                  <div style={css(v.ketDoekVakStyle)}>
                    <canvas
                      ref={v.ketRef} width={v.ketVak} height={v.ketVak}
                      onPointerDown={v.ketDown} onPointerMove={v.ketMove}
                      onPointerUp={v.ketUp} onPointerCancel={v.ketUp}
                      style={css(v.ketDoekStyle)}
                    />
                    {v.ketSelVak ? (
                      <svg style={css(v.ketSelSvgStyle)} viewBox="0 0 32 32" preserveAspectRatio="none" aria-hidden="true">
                        <rect
                          x={v.ketSelVak.x} y={v.ketSelVak.y} width={v.ketSelVak.w} height={v.ketSelVak.h}
                          fill="rgba(124,92,255,.18)" stroke="var(--paars)" strokeWidth="3"
                          vectorEffect="non-scaling-stroke" strokeDasharray="6 6" strokeLinecap="square"
                          style={css(v.ketSelVakStyle)}
                        />
                      </svg>
                    ) : null}
                  </div>
                  <div style={css(v.ketVakStyleRij)}>
                    {toList(v.ketKleuren).map((k, $index) => (
                      <React.Fragment key={$index}>
                        <button onClick={k?.onClick} className="gp-swatch" title={k?.titel} aria-label={k?.titel} aria-pressed={k?.gekozen} style={css(k?.style)} />
                      </React.Fragment>
                    ))}
                    <button onClick={v.ketPakPen} className="gp-btn" title="Tekenen" aria-label="Tekenen" style={css(v.ketPenStyle)}>
                      <Potlood maat={16} />
                    </button>
                    <button onClick={v.ketPakKader} className="gp-btn" title="Een vlak aanwijzen" aria-label="Een vlak aanwijzen" style={css(v.ketKaderStyle)}>
                      <Kader maat={16} />
                    </button>
                    <button onClick={v.ketPakGum} className="gp-btn" title="Gum" aria-label="Gum" aria-pressed={v.ketGumGekozen} style={css(v.ketGumStyle)}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.3" strokeLinecap="round" strokeLinejoin="round">
                        <path d="m7 21-4.3-4.3c-1-1-1-2.5 0-3.4l9.6-9.6c1-1 2.5-1 3.4 0l5.6 5.6c1 1 1 2.5 0 3.4L13 21" />
                        <path d="M22 21H7" />
                      </svg>
                    </button>
                    <button onClick={v.ketWis} className="gp-btn" title="Opnieuw beginnen" aria-label="Opnieuw beginnen" style={css(v.ketWisStyle)}>
                      <Herstel maat={16} />
                    </button>
                  </div>
                </React.Fragment>
              ) : null}

              {v.showKetTempo ? (
                <div style={css(v.ketTempoRijStyle)}>
                  {toList(v.ketTempo).map((t, $index) => (
                    <React.Fragment key={$index}>
                      <button onClick={t?.onClick} className="gp-btn" style={css(t?.style)}>
                        <span style={css(v.ketTempoLabelStyle)}><Interp v={t?.label} /></span>
                        <span style={css(v.ketTempoRegelStyle)}><Interp v={t?.regel} /></span>
                      </button>
                    </React.Fragment>
                  ))}
                </div>
              ) : null}

              {v.showKetFout ? (
                <div style={css(v.ketFoutStyle)}><Interp v={v.ketFout} /></div>
              ) : null}

              <button onClick={v.ketKlaarKnop} disabled={v.ketKlaarUit} className="gp-btn pink" style={css(v.ketKlaarStyle)}>
                <Interp v={v.ketKlaarLabel} />
              </button>
              {v.showKetLeesKnop ? (
                <button onClick={v.ketNaarLijst} style={css(v.ketLeesKnopStyle)}><Interp v={v.ketLeesKnopLabel} /></button>
              ) : null}
            </React.Fragment>
          ) : null}

          {v.showKetKiezen ? (
            <React.Fragment>
              <div style={css(v.ketKopStyle)}><Interp v={v.ketKiesKop} /></div>
              {v.showKetKiesLeeg ? (
                <div style={css(v.ketLijstLeegStyle)}><Interp v={v.ketKiesLeeg} /></div>
              ) : null}
              <div onMouseLeave={v.ketWijstWeg} style={css(v.ketKiesRasterStyle)}>
                {toList(v.ketKandidaten).map((a, $index) => (
                  <React.Fragment key={$index}>
                    <button onClick={a?.onPick} onMouseEnter={a?.onHover} onFocus={a?.onHover} disabled={v.ketBezig} className="gp-swatch" title={a?.name} aria-label={a?.name} style={css(a?.style)}>
                      <img alt="" src={a?.avatarUrl} style={css(a?.avStyle)} />
                      <span style={css(a?.stipStyle)} />
                    </button>
                  </React.Fragment>
                ))}
              </div>
              <div style={css(v.ketWijstStyle)}><Interp v={v.ketWijstNaam} /></div>
              {v.showKetFout ? (
                <div style={css(v.ketFoutStyle)}><Interp v={v.ketFout} /></div>
              ) : null}
              <button onClick={v.ketKiesTerug} style={css(v.ketKiesTerugStyle)}><Interp v={v.ketKiesTerugLabel} /></button>
            </React.Fragment>
          ) : null}

          {v.showKetLijst ? (
            <React.Fragment>
              <div style={css(v.ketKopStyle)}>Teruglezen</div>
              {v.showKetLijstLeeg ? (
                <div style={css(v.ketLijstLeegStyle)}><Interp v={v.ketLijstLeeg} /></div>
              ) : null}
              <div style={css(v.ketLijstStyle)}>
                {toList(v.ketKlaarLijst).map((k, $index) => (
                  <React.Fragment key={$index}>
                    <button onClick={k?.onClick} className="gp-btn" style={css(k?.style)}>
                      <span><Interp v={k?.naam} /></span>
                      <span style={css(k?.wanneerStyle)}><Interp v={k?.wanneer} /></span>
                    </button>
                  </React.Fragment>
                ))}
              </div>
              <button onClick={v.ketUitLijst} className="gp-btn" style={css(v.ketTerugStyle)}>Terug</button>
            </React.Fragment>
          ) : null}

          {v.showKetLees ? (
            <React.Fragment>
              <div style={css(v.ketKopStyle)}><Interp v={v.ketLeesKop} /></div>
              <div style={css(v.ketLeesStrookStyle)}>
                {toList(v.ketLeesStappen).map((s, $index) => (
                  <React.Fragment key={$index}>
                    <div style={css(s?.style)}>
                      {s?.isZin
                        ? <div style={css(s?.zinStyle)}><Interp v={s?.tekst} /></div>
                        : <img alt="" src={s?.src} style={css(s?.krabbelStyle)} />}
                      <div style={css(s?.naamStyle)}><Interp v={s?.naam} /></div>
                    </div>
                  </React.Fragment>
                ))}
              </div>
              {v.showKetLeesSlot ? (
                <div style={css(v.ketLeesSlotStyle)}><Interp v={v.ketLeesSlot} /></div>
              ) : null}
              <button onClick={v.ketAf} className="gp-btn" style={css(v.ketTerugStyle)}>Terug</button>
            </React.Fragment>
          ) : null}
        </div>
    </Venster>
  );
}
